import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import AdminDashboard         from './AdminDashboard';
import MembersPage            from './MembersPage';
import AddMemberPage          from './AddMemberPage';
import AttendancePage         from './AttendancePage';
import FinancialsPage         from './FinancialsPage';
import RecordContributionPage from './RecordContributionPage';
import FinancialModulePage    from './FinancialModulePage';
import HouseholdsPage         from './HouseholdsPage';
import ReportsPage            from './ReportsPage';
import CalendarPage           from './CalendarPage';
import TrackingPage from './TrackingPage';
import InventoryPage from './InventoryPage';
import SettingsPage from './SettingsPage';

function NavItem({ icon, label, to, count, navigate, location, onClick }) {
  const active =
    location.pathname === to ||
    (to !== '/admin' && location.pathname.startsWith(to));
  return (
    <button
      className={`nav-item${active ? ' active' : ''}`}
      onClick={() => { navigate(to); onClick && onClick(); }}
    >
      <span style={{ fontSize: 15 }}>{icon}</span>
      {label}
      {count > 0 && <span className="nav-count">{count}</span>}
    </button>
  );
}

export default function AdminLayout() {
  const { profile, member, signOut, isFinance } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [sideOpen, setSideOpen] = useState(false);

  useEffect(() => { setSideOpen(false); }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = sideOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sideOpen]);

  const name = member
    ? `${member.first_name} ${member.last_name}`
    : profile?.email || 'Admin';
  const init = name[0].toUpperCase();

  function logout()    { signOut(); navigate('/login'); }
  function closeMenu() { setSideOpen(false); }

  return (
    <div className="app-layout">

      {/* Dark overlay on mobile when sidebar open */}
      {sideOpen && (
        <div onClick={closeMenu} style={{
          position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:199,
        }} />
      )}

      {/* Sidebar */}
      <div className={`sidebar${sideOpen ? ' open' : ''}`}>
        <div className="sidebar-logo">
          <div className="s-logo-inner">
             <img src="/pcea-logo.png" alt="PCEA" style={{ width:28, height:28, objectFit:'contain' }} className="mobile-hide" />
            
            <div>
              <div className="s-title">PCEA Thing'ati</div>
              <div className="s-sub">Admin Panel</div>
            </div>
          </div>
        </div>

        <div style={{ flex:1, paddingBottom:8 }}>
          <div className="nav-section">Overview</div>
          <NavItem icon="📊" label="Dashboard"          to="/admin"                   navigate={navigate} location={location} onClick={closeMenu} />

          <div className="nav-section">Members</div>
          <NavItem icon="👥" label="All Members"        to="/admin/members"           navigate={navigate} location={location} onClick={closeMenu} />
          <NavItem icon="🏠" label="Households"          to="/admin/households"         navigate={navigate} location={location} onClick={closeMenu} />
          <NavItem icon="⏳" label="Pending Approvals"  to="/admin/members/pending"   navigate={navigate} location={location} onClick={closeMenu} />
          <NavItem icon="➕" label="Add Member"         to="/admin/members/add"       navigate={navigate} location={location} onClick={closeMenu} />
          <NavItem icon="📋" label="Attendance"         to="/admin/attendance"        navigate={navigate} location={location} onClick={closeMenu} />
          <NavItem icon="📈📉" label="Member Tracking"    to="/admin/tracking"          navigate={navigate} location={location} onClick={closeMenu} />

          {isFinance && <>
            <div className="nav-section">Finance</div>
            <NavItem icon="💰" label="Financial Overview"   to="/admin/financials"         navigate={navigate} location={location} onClick={closeMenu} />
            <NavItem icon="📝" label="Record Contribution"  to="/admin/financials/record"  navigate={navigate} location={location} onClick={closeMenu} />
            <NavItem icon="🏦" label="Financial Module"   to="/admin/finance"            navigate={navigate} location={location} onClick={closeMenu} />
          </>}

          <div className="nav-section">Church Life</div>
          <NavItem icon="📅" label="Church Calendar"    to="/admin/calendar"          navigate={navigate} location={location} onClick={closeMenu} />

          <div className="nav-section">Inventory</div>
          <NavItem icon="🗄️" label="View Inventory"   to="/admin/inventory"           navigate={navigate} location={location} onClick={closeMenu} />

          <div className="nav-section">Reports</div>
          <NavItem icon="📄" label="Generate Reports"   to="/admin/reports"           navigate={navigate} location={location} onClick={closeMenu} />

          {/* System section moved inside the flex container */}
          <div className="nav-section">System</div>
          <NavItem icon="⚙️" label="Settings"   to="/admin/settings"           navigate={navigate} location={location} onClick={closeMenu} />
        </div>

        <div className="sidebar-footer">
          <div className="user-chip">
            <div className="avatar">{init}</div>
            <div className="user-info" style={{ flex:1, minWidth:0 }}>
              <div className="user-name">{name}</div>
              <div className="user-role">{profile?.role?.replace(/_/g,' ')}</div>
            </div>
            <button onClick={logout} title="Logout"
              style={{ background:'none', border:'none', color:'rgba(255,255,255,.5)', cursor:'pointer', fontSize:16 }}>
              ⏏
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="main-wrap">
        <div className="topbar">
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <button className="hamburger-btn" onClick={() => setSideOpen(o => !o)} aria-label="Open menu">☰</button>
             <img src="/pcea-logo.png" alt="PCEA" style={{ width:28, height:28, objectFit:'contain' }} className="mobile-hide" />
            <div className="topbar-title">PCEA Thing'ati</div>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:12, color:'var(--text3)' }} className="mobile-hide">
              {new Date().toLocaleDateString('en-KE',{ weekday:'long', day:'numeric', month:'long', year:'numeric' })}
            </span>
            <button className="btn btn-outline btn-sm no-print" onClick={logout}>Logout</button>
          </div>
        </div>

        <div className="page-content">
          <Routes>
            <Route index                    element={<AdminDashboard />} />
            <Route path="members"           element={<MembersPage />} />
            <Route path="households"        element={<HouseholdsPage />} />
            <Route path="members/pending"   element={<MembersPage pendingOnly />} />
            <Route path="members/add"       element={<AddMemberPage />} />
            <Route path="attendance"        element={<AttendancePage />} />
            <Route path="tracking"          element={<TrackingPage />} />
            <Route path="financials"        element={<FinancialsPage />} />
            <Route path="finance/*"         element={<FinancialModulePage />} />
            <Route path="financials/record" element={<RecordContributionPage />} />
            <Route path="calendar"          element={<CalendarPage />} />
            <Route path="inventory"         element={<InventoryPage />} />
            <Route path="reports"           element={<ReportsPage />} />
            <Route path="settings"          element={<SettingsPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}